import { formatBytes } from '@/components/util';

const isNonEmptySlicesJsonString = (s: unknown): s is string =>
  typeof s === 'string' && s.trim() !== '' && s !== '[]';

/**
 * Azure blob pointers written by the server (`audioBlobContainerName` + `audioBlobPaths`).
 * Paths may be a string array in Mongo or a JSON string after some transports.
 */
export const therapistRecordingHasBlobAudioPointers = (
  recording: Record<string, unknown>,
): boolean => {
  const container = recording.audioBlobContainerName;
  if (typeof container !== 'string' || container.trim() === '') {
    return false;
  }
  const paths = recording.audioBlobPaths;
  if (Array.isArray(paths)) {
    return paths.some(p => typeof p === 'string' && p.trim() !== '');
  }
  if (typeof paths === 'string' && paths.trim() !== '') {
    try {
      const parsed = JSON.parse(paths) as unknown;
      if (Array.isArray(parsed)) {
        return parsed.some(
          p => typeof p === 'string' && String(p).trim() !== '',
        );
      }
    } catch {
      return true;
    }
  }
  return false;
};

const therapistRecordingHasInlineOrSliceAudio = (
  recording: Record<string, unknown>,
): boolean => {
  const slices = recording.uploadedAudioContentSlices;
  if (typeof slices === 'number' && slices > 0) {
    return true;
  }
  if (Array.isArray(slices) && slices.length > 0) {
    return true;
  }
  if (isNonEmptySlicesJsonString(slices)) {
    try {
      const parsed = JSON.parse(slices) as unknown;
      if (Array.isArray(parsed) && parsed.length > 0) {
        return true;
      }
    } catch {
      return true;
    }
  }
  return !!recording.uploadedAudioContent;
};

/**
 * Admin "Force transcribe": when audio is still retrievable (blob or inline/slices) and
 * the recording is not already `transcribed` (no need to retry a completed run).
 */
export const therapistRecordingShouldShowAdminForceTranscribe = (
  recording: Record<string, unknown>,
): boolean => {
  if (String(recording.status ?? '') === 'transcribed') {
    return false;
  }
  if (therapistRecordingHasBlobAudioPointers(recording)) {
    return true;
  }
  return therapistRecordingHasInlineOrSliceAudio(recording);
};

/**
 * Mirrors server checks in `therapistRecordingsTranscribeOne` / `ensureTherapistRecordingAudioLoaded`:
 * audio can be retried when DB slices exist, legacy single `uploadedAudioContent`, or Azure blob pointers.
 *
 * Admin list queries often project `uploadedAudioContentSlices` as a numeric **byte sum** (aggregation),
 * not an array — treat `number > 0` as present.
 */
export const therapistRecordingHasRetrievableAudio = (
  recording: Record<string, unknown>,
): boolean =>
  therapistRecordingHasBlobAudioPointers(recording) ||
  therapistRecordingHasInlineOrSliceAudio(recording);

const blobPathCount = (recording: Record<string, unknown>): number => {
  const paths = recording.audioBlobPaths;
  if (Array.isArray(paths)) {
    return paths.filter(
      p => typeof p === 'string' && p.trim() !== '',
    ).length;
  }
  if (typeof paths === 'string' && paths.trim() !== '') {
    try {
      const parsed = JSON.parse(paths) as unknown;
      if (Array.isArray(parsed)) {
        return parsed.filter(
          p => typeof p === 'string' && String(p).trim() !== '',
        ).length;
      }
    } catch {
      return 1;
    }
  }
  return 0;
};

/** Short label for the "Audio Content" row (plaintext byte length when set, else projection/slices/blob). */
export const therapistRecordingAudioContentSummary = (
  recording: Record<string, unknown>,
): string | null => {
  const plainLen = recording.unencryptedAudioByteLength;
  if (typeof plainLen === 'number' && plainLen > 0) {
    return formatBytes(plainLen);
  }

  const slices = recording.uploadedAudioContentSlices;
  if (typeof slices === 'number' && slices > 0) {
    return formatBytes(slices);
  }
  if (Array.isArray(slices) && slices.length > 0) {
    return `${slices.length} slice(s)`;
  }
  if (isNonEmptySlicesJsonString(slices)) {
    try {
      const parsed = JSON.parse(slices) as unknown;
      if (Array.isArray(parsed) && parsed.length > 0) {
        return `${parsed.length} slice(s)`;
      }
    } catch {
      return 'Audio slices (opaque)';
    }
  }
  if (recording.uploadedAudioContent) {
    return 'Encrypted audio (inline)';
  }
  if (therapistRecordingHasBlobAudioPointers(recording)) {
    const container = recording.audioBlobContainerName as string;
    const n = blobPathCount(recording);
    return `Blob ${container} · ${n} object(s)`;
  }
  return null;
};
